import {useParams} from 'react-router-dom';
import {useState, useCallback, useMemo} from 'react';
import {useQuery} from '@apollo/client';
import {Table, Pagination, Typography, Tag} from 'antd';
import {REFERRAL_PARTNER_REFEREES} from '../resolvers';
import {debounce, sanitizeInput} from 'utils/helper';
import {currencySymbols} from 'utils/mock';
import {toast} from 'react-toastify';
import moment from 'moment';
import styles from './userPartnerReferrals.module.scss';
import {Search} from '@dabafinance/react-components';

const {Title, Text} = Typography;

export const UserPartnerReferrals = () => {
  const {id: partnerId} = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  const {data, loading, refetch} = useQuery(REFERRAL_PARTNER_REFEREES, {
    variables: {
      input: {
        partnerId,
        page: currentPage,
        limit,
        ...(searchTerm && {search: sanitizeInput(searchTerm)}),
      },
    },
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const refereesData =
    data?.referralPartnerGetReferees?.__typename ===
    'ReferralPartnerRefereesResponse'
      ? data.referralPartnerGetReferees
      : null;

  const referees = refereesData?.referees || [];
  const totalDocs = refereesData?.totalDocs || 0;
  const currentPageNum = refereesData?.page || 1;

  const handleSearch = useCallback(
    debounce(value => {
      const sanitizedValue = sanitizeInput(value);
      setSearchTerm(sanitizedValue);
      setCurrentPage(1);
      refetch({
        input: {
          partnerId,
          page: 1,
          limit,
          ...(sanitizedValue && {search: sanitizedValue}),
        },
      });
    }),
    [partnerId, limit, refetch],
  );

  const handlePageChange = (page, pageSize) => {
    if (page === currentPage && pageSize === limit) return;
    setCurrentPage(page);
    setLimit(pageSize);
    refetch({
      input: {
        partnerId,
        page,
        limit: pageSize,
        ...(searchTerm && {search: sanitizeInput(searchTerm)}),
      },
    });
  };

  const columns = useMemo(
    () => [
      {
        title: 'Full Name',
        key: 'fullName',
        dataIndex: 'fullName',
        fixed: 'left',
        render: name => (
          <span className={styles.fullName}>{name || 'Unknown'}</span>
        ),
        width: 180,
      },
      {
        title: 'Email',
        key: 'email',
        dataIndex: 'email',
        render: email => <span className={styles.email}>{email || '-'}</span>,
        width: 250,
      },
      {
        title: 'Date Signed Up',
        key: 'dateSignedUp',
        dataIndex: 'dateSignedUp',
        render: date => (
          <span className={styles.date}>
            {date ? moment(date).format('MMM DD, YYYY') : '-'}
          </span>
        ),
        width: 150,
      },
      {
        // `hasSettledOrder` now means investment activity only. A referee who
        // bought a course but never invested is false here and true under
        // "Bought Academy", so this column must not read as "Active".
        title: 'Has Invested',
        key: 'hasSettledOrder',
        dataIndex: 'hasSettledOrder',
        render: hasSettledOrder => (
          <Tag
            color={hasSettledOrder ? 'green' : 'default'}
            className={styles.status}>
            {hasSettledOrder ? 'Invested' : 'Not yet'}
          </Tag>
        ),
        width: 130,
      },
      {
        title: 'Bought Academy',
        key: 'hasPurchasedAcademy',
        dataIndex: 'hasPurchasedAcademy',
        render: hasPurchasedAcademy => (
          <Tag
            color={hasPurchasedAcademy ? 'purple' : 'default'}
            className={styles.status}>
            {hasPurchasedAcademy ? 'Purchased' : 'No'}
          </Tag>
        ),
        width: 140,
      },
      {
        title: 'Total Invested',
        key: 'totalInvested',
        dataIndex: 'totalInvested',
        render: amount => {
          // Assuming XOF currency, but you might want to get this from the query
          const currency = 'XOF';
          const currencySymbol = currencySymbols?.[currency]?.symbol || 'FCFA';
          return (
            <span className={styles.amount}>
              {currencySymbol}
              {Number(amount || 0).toLocaleString('en-US')}
            </span>
          );
        },
        width: 150,
        align: 'right',
      },
      {
        title: 'Academy Spend',
        key: 'totalAcademySpend',
        dataIndex: 'totalAcademySpend',
        render: amount => {
          const currencySymbol = currencySymbols?.XOF?.symbol || 'FCFA';
          return (
            <span className={styles.amount}>
              {currencySymbol}
              {Number(amount || 0).toLocaleString('en-US')}
            </span>
          );
        },
        width: 150,
        align: 'right',
      },
    ],
    [],
  );

  if (data?.referralPartnerGetReferees?.__typename === 'Error') {
    return (
      <div className={styles.errorContainer}>
        <Text type="danger">
          {data.referralPartnerGetReferees.message ||
            'Failed to load referees data'}
        </Text>
      </div>
    );
  }

  return (
    <div className={styles.referrals}>
      <div className={styles.header}>
        <div className={styles.titleWrap}>
          <Title level={2}>Referees</Title>
          <Text type="secondary">Manage and track your referred partners</Text>
        </div>
      </div>

      <div className={styles.searchContainer}>
        <Search
          placeholder="Search by name or email..."
          onChange={value => handleSearch(value)}
          className={styles.searchInput}
        />
      </div>

      <div className={styles.tableContainer}>
        <Table
          columns={columns}
          dataSource={referees}
          loading={loading}
          rowKey="id"
          pagination={false}
          scroll={{x: 'max-content'}}
          className={styles.table}
        />

        {totalDocs > 0 && (
          <div className={styles.paginationContainer}>
            <Text type="secondary" className={styles.paginationInfo}>
              Showing page {currentPageNum} of {Math.ceil(totalDocs / limit)} (
              {totalDocs} total referees)
            </Text>
            <Pagination
              current={currentPageNum}
              total={totalDocs}
              pageSize={limit}
              showSizeChanger
              pageSizeOptions={['10', '20', '50', '100']}
              onChange={handlePageChange}
              onShowSizeChange={handlePageChange}
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total} items`
              }
              className={styles.pagination}
            />
          </div>
        )}

        {!loading && referees.length === 0 && (
          <div className={styles.emptyState}>
            <Text type="secondary">
              {searchTerm
                ? 'No referees found matching your search'
                : 'No referees found'}
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};
